import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PEOPLE_RULES, anyPlant, useEnforcedCan } from '../../access';
import { useSignedInUser, useViewingAs } from '../../session';
import { useLogout } from '../../utilities/customHooks/useLogout';

import { PROFILE_MENU_ROUTES } from './header.constants';
import { visibleProfileMenuItems, type ProfileMenuItemId } from './header.rules';
import { useStopViewAs } from './useViewAsSwitch';

export type ProfileDialog = 'viewAs';

type ProfileOverlay =
  | { kind: 'closed' }
  | { kind: 'menu'; anchor: HTMLElement }
  | { kind: 'dialog'; dialog: ProfileDialog };

const CLOSED: ProfileOverlay = { kind: 'closed' };

export interface ProfileMenuItem {
  id: ProfileMenuItemId;
  onSelect: () => void;
}

export interface ProfileMenuLogic {
  userName: string;
  menuAnchor: HTMLElement | null;
  dialog: ProfileDialog | null;
  items: readonly ProfileMenuItem[];
  openMenu: (anchor: HTMLElement) => void;
  close: () => void;
}

export const useProfileMenu = (): ProfileMenuLogic => {
  const navigate = useNavigate();
  const { logOut } = useLogout();
  const stopViewing = useStopViewAs();
  const canViewAs = useEnforcedCan(PEOPLE_RULES.viewAs, anyPlant);
  const { name: userName } = useSignedInUser();
  const isViewing = useViewingAs() !== null;
  const [overlay, setOverlay] = useState<ProfileOverlay>(CLOSED);

  const close = () => setOverlay(CLOSED);
  const closeThen = (action: () => void) => () => {
    close();
    action();
  };
  const goTo = (to: string) => closeThen(() => navigate(to));
  const openDialog = (dialog: ProfileDialog) => () => setOverlay({ kind: 'dialog', dialog });

  const handlers: Record<ProfileMenuItemId, () => void> = {
    profile: goTo(PROFILE_MENU_ROUTES.profile),
    notifications: goTo(PROFILE_MENU_ROUTES.notifications),
    logout: closeThen(logOut),
    stopViewAs: closeThen(stopViewing),
    viewAs: openDialog('viewAs'),
    about: goTo(PROFILE_MENU_ROUTES.about),
  };

  const items = visibleProfileMenuItems({ canViewAs, isViewing }).map((id) => ({
    id,
    onSelect: handlers[id],
  }));

  return {
    userName,
    menuAnchor: overlay.kind === 'menu' ? overlay.anchor : null,
    dialog: overlay.kind === 'dialog' ? overlay.dialog : null,
    items,
    openMenu: (anchor) => setOverlay({ kind: 'menu', anchor }),
    close,
  };
};
